import { Controller } from "@hotwired/stimulus"
import { marked } from "marked"

// Configure marked for chat output
marked.setOptions({
    breaks: true,     // Convert \n to <br> (GFM line breaks)
    gfm: true         // GitHub Flavored Markdown (tables, strikethrough, etc.)
})

export default class extends Controller {
    static targets = ["window", "messages", "input", "form", "expandBtn", "collapseBtn"]
    static values = { conversationId: Number }

    connect() {
        console.log("AI Chat Controller Connected")
        this.isOpen = false
        this.isExpanded = false
        this.conversationIdValue = null
        this.isLoading = false

        // Add retry tracking
        this.retryCount = 0
        this.maxRetries = 2

        // Typewriter effect queue
        this.tokenQueue = []
        this.isRendering = false
        this.renderInterval = null
        this.renderedContent = ''
        this.TOKEN_DELAY_MS = 20

        // Scroll throttling
        this._scrollRAF = null

        // Smooth completion
        this.pendingFinalContent = null
        this.onQueueDrain = null
    }


    disconnect() {
        this.stopTokenQueue()
        if (this._scrollRAF) {
            cancelAnimationFrame(this._scrollRAF)
            this._scrollRAF = null
        }
    }

    toggle() {
        console.log("Toggle clicked, isOpen:", this.isOpen)

        if (this.isOpen) {
            this.close()
        } else {
            this.open()
        }
    }

    open() {
        console.log("Opening chat window")
        this.windowTarget.style.display = 'flex'
        this.isOpen = true

        // Focus input for better UX
        if (this.hasInputTarget) {
            setTimeout(() => this.inputTarget.focus(), 100)
        }

        // Load conversation if we don't have one
        if (!this.conversationIdValue) {
            this.loadOrCreateConversation()
        }
    }

    close() {
        console.log("Closing chat window")
        // If expanded, collapse first
        if (this.isExpanded) {
            this.collapseToPanel()
        }
        this.windowTarget.style.display = 'none'
        this.isOpen = false
    }

    expand() {
        console.log("Expanding chat to panel")
        if (!this.isOpen) {
            this.open()
        }
        this.isExpanded = true
        this.element.classList.add('expanded')
        document.body.classList.add('chat-expanded')
        this.updateButtonVisibility()
    }

    collapse() {
        console.log("Collapsing chat to bubble")
        this.collapseToPanel()
    }

    collapseToPanel() {
        this.isExpanded = false
        this.element.classList.remove('expanded')
        document.body.classList.remove('chat-expanded')
        this.updateButtonVisibility()
    }

    updateButtonVisibility() {
        const expandBtn = this.element.querySelector('.chat-expand-btn')
        const collapseBtn = this.element.querySelector('.chat-collapse-btn')

        if (expandBtn && collapseBtn) {
            if (this.isExpanded) {
                expandBtn.style.display = 'none'
                collapseBtn.style.display = 'flex'
            } else {
                expandBtn.style.display = 'flex'
                collapseBtn.style.display = 'none'
            }
        }
    }

    // Enhanced sendMessage with streaming support
    async sendMessage(event) {
        event.preventDefault()

        if (this.isLoading) return

        const message = this.inputTarget.value.trim()
        if (!message) return

        console.log("Sending message:", message)
        this.retryCount = 0 // Reset retry count for new message

        // Disable form while processing
        this.setLoading(true)

        try {
            // Add user message to UI immediately
            this.addMessage(message, 'user')
            this.inputTarget.value = ''

            // Ensure we have a conversation
            if (!this.conversationIdValue) {
                await this.createConversation()
            }

            // Use streaming by default, with fallback to non-streaming
            try {
                console.log('[Chat] Attempting streaming...')
                await this.submitMessageStreaming(message)
                console.log('[Chat] Streaming completed successfully')
            } catch (streamError) {
                console.warn('[Chat] Streaming failed, falling back to regular request:', streamError)
                // Show typing indicator for fallback
                this.showTypingIndicator()
                await this.submitMessageWithRetry(message)
            }

        } catch (error) {
            console.error('Error sending message:', error)
            this.handleSendError(error)
        } finally {
            this.hideTypingIndicator()
            this.removeStreamingCursor()
            this.setLoading(false)
        }
    }


    async loadOrCreateConversation() {
        try {
            const response = await fetch('/ai_conversations/current.json', {
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': this.getCSRFToken()
                }
            })

            if (response.ok) {
                const data = await response.json()
                if (data.conversation) {
                    this.conversationIdValue = data.conversation.id
                    this.loadMessages(data.messages || [])
                }
            } else {
                console.warn('Failed to load conversation:', response.status, response.statusText)
            }
        } catch (error) {
            console.log('No existing conversation, will create on first message:', error.message)
        }
    }

    async createConversation() {
        const response = await fetch('/ai_conversations', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-Token': this.getCSRFToken()
            },
            body: JSON.stringify({})
        })

        if (response.ok) {
            const data = await response.json()
            this.conversationIdValue = data.id
            console.log("Created conversation:", this.conversationIdValue)
        } else {
            const errorText = await response.text()
            console.error('Failed to create conversation:', response.status, errorText)
            throw new Error('Failed to create conversation')
        }
    }

    // Retries with simple exponential backoff (500ms, 1000ms, 2000ms)
    async submitMessageWithRetry(message, maxAttempts = 3) {
        let attempt = 0
        let lastError

        while (attempt < maxAttempts) {
            try {
                return await this.submitMessage(message)
            } catch (err) {
                lastError = err
                attempt += 1
                if (attempt >= maxAttempts) break

                const delayMs = 500 * Math.pow(2, attempt - 1)
                console.warn(`submitMessage retry ${attempt}/${maxAttempts} after ${delayMs}ms`, err)
                await new Promise((r) => setTimeout(r, delayMs))
            }
        }

        throw lastError || new Error('Failed to send message after retries')
    }

    async submitMessage(content) {
        if (!this.conversationIdValue) {
            throw new Error('No conversation available')
        }

        const url = `/ai_conversations/${encodeURIComponent(this.conversationIdValue)}/message.json`
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-Token': this.getCSRFToken()
            },
            body: JSON.stringify({ message: content })
        })

        if (!response.ok) {
            const errorText = await response.text().catch(() => '')
            console.error('Failed to send message:', response.status, errorText)

            try {
                const errorData = JSON.parse(errorText)
                throw new Error(errorData.error || `HTTP ${response.status}`)
            } catch {
                throw new Error('Server error - please try again')
            }
        }

        const data = await response.json()
        const assistantText = data?.assistant_message || data?.response || ''
        const assistantMessageId = data?.assistant_message_id

        // Use typewriter effect even for non-streaming fallback
        console.warn('[Chat] Using non-streaming fallback — applying typewriter to response')
        this.hideTypingIndicator()
        const messageContainer = this.createStreamingMessageContainer()
        const contentElement = messageContainer.querySelector('.streaming-content')
        this.enqueueToken(assistantText || 'No response received.', contentElement)

        // Set up drain callback to finalize and add feedback buttons
        this.onQueueDrain = () => {
            messageContainer.classList.remove('streaming')
            if (assistantMessageId) {
                messageContainer.appendChild(this.createFeedbackButtons(assistantMessageId))
            }
        }

        return assistantText
    }

    // Streaming message submission using SSE
    async submitMessageStreaming(content) {
        if (!this.conversationIdValue) {
            throw new Error('No conversation available')
        }

        const url = `/ai_conversations/${encodeURIComponent(this.conversationIdValue)}/stream_message`

        // Create streaming message container (with typing dots)
        const messageContainer = this.createStreamingMessageContainer()

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'text/event-stream',
                    'X-CSRF-Token': this.getCSRFToken()
                },
                body: JSON.stringify({ message: content })
            })

            if (!response.ok) {
                const errorText = await response.text().catch(() => '')
                console.error('Streaming request failed:', response.status, errorText)

                try {
                    const errorData = JSON.parse(errorText)
                    throw new Error(errorData.error || `HTTP ${response.status}`)
                } catch (e) {
                    if (e.message.includes('limit')) throw e
                    throw new Error('Server error - please try again')
                }
            }

            // Process the SSE stream
            await this.processStreamResponse(response, messageContainer)

        } catch (error) {
            // Remove the streaming container and show error
            if (messageContainer && messageContainer.parentNode) {
                messageContainer.remove()
            }
            throw error
        }
    }

    // Process SSE stream response
    async processStreamResponse(response, messageContainer) {
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        let fullContent = ''

        try {
            while (true) {
                const { done, value } = await reader.read()

                if (done) break

                buffer += decoder.decode(value, { stream: true })

                // Process complete SSE events
                const lines = buffer.split('\n')
                buffer = lines.pop() || '' // Keep incomplete line in buffer

                let eventType = null
                for (const line of lines) {
                    if (line.startsWith('event: ')) {
                        eventType = line.slice(7).trim()
                    } else if (line.startsWith('data: ') && eventType) {
                        const data = line.slice(6)
                        try {
                            const parsed = JSON.parse(data)
                            fullContent = this.handleStreamEvent(eventType, parsed, messageContainer, fullContent)
                        } catch (e) {
                            console.warn('Failed to parse SSE data:', e)
                        }
                        eventType = null
                    }
                }
            }
        } finally {
            reader.releaseLock()
        }

        // Remove streaming cursor when done
        this.removeStreamingCursor()
    }

    // Handle individual SSE events
    handleStreamEvent(eventType, data, messageContainer, currentContent) {
        const contentElement = messageContainer.querySelector('.streaming-content')

        switch (eventType) {
            case 'token':
                // Queue token for typewriter rendering
                currentContent += data
                console.log(`[Chat] Token received (${data.length} chars), queue: ${this.tokenQueue.length}`)
                this.enqueueToken(data, contentElement)
                break

            case 'complete':
                // The complete event carries the final processed response.
                // This may contain command execution results that weren't streamed as tokens.
                const redirectMatch = data.match(/\[REDIRECT:([^\]]+)\]/)
                const cleanData = data.replace(/\[REDIRECT:[^\]]+\]/, '')

                currentContent = cleanData
                this.pendingFinalContent = cleanData

                // Store redirect for after content finishes rendering
                const pendingRedirect = redirectMatch ? redirectMatch[1] : null

                const applyFinalAndRedirect = () => {
                    messageContainer.classList.remove('streaming')

                    // Add feedback buttons if we have a message ID
                    const msgId = messageContainer.dataset.messageId
                    if (msgId && !messageContainer.querySelector('.message-feedback')) {
                        messageContainer.appendChild(this.createFeedbackButtons(msgId))
                    }

                    this.scrollToBottom()

                    if (pendingRedirect) {
                        const redirectMsg = document.createElement('div')
                        redirectMsg.style.cssText = 'text-align: center; margin: 10px 0; padding: 8px; background: #f0f9ff; border-radius: 8px; font-size: 13px; color: #0369a1;'
                        redirectMsg.innerHTML = 'Opening logbook entry in 1 second... <span style="font-size: 11px;">(or click link above)</span>'
                        this.messagesTarget.appendChild(redirectMsg)
                        this.scrollToBottom()

                        setTimeout(() => {
                            window.location.href = pendingRedirect
                        }, 1000)
                    }
                }

                // Check if the complete content differs from what was streamed
                // (e.g., commands were processed and replaced with results)
                const alreadyRendered = this.renderedContent + this.tokenQueue.join('')
                const hasNewContent = cleanData.trim() !== alreadyRendered.trim()

                if (hasNewContent) {
                    // Content differs — stop current queue, typewrite the new content
                    this.stopTokenQueue()
                    this.tokenQueue = []
                    this.renderedContent = ''
                    if (contentElement) {
                        contentElement.innerHTML = ''
                    }

                    // Enqueue the processed result for typewriter rendering
                    this.enqueueToken(cleanData, contentElement)
                    this.onQueueDrain = applyFinalAndRedirect
                } else {
                    // Content matches what was streamed — let queue drain naturally
                    if (this.tokenQueue.length === 0 && !this.renderInterval) {
                        // Already done rendering
                        if (contentElement) {
                            this.renderedContent = cleanData
                            contentElement.innerHTML = this.formatContent(cleanData)
                        }
                        applyFinalAndRedirect()
                    } else {
                        this.pendingFinalContent = cleanData
                        this.onQueueDrain = () => {
                            if (contentElement) {
                                this.renderedContent = this.pendingFinalContent || cleanData
                                contentElement.innerHTML = this.formatContent(this.renderedContent)
                            }
                            this.pendingFinalContent = null
                            applyFinalAndRedirect()
                        }
                    }
                }
                break

            case 'links':
                // Links arrive after complete — append to final content and
                // let it apply when queue drains (or immediately if already drained)
                if (contentElement && data) {
                    currentContent += data
                    const linksContent = currentContent
                    if (this.tokenQueue.length === 0 && !this.renderInterval) {
                        this.renderedContent = linksContent
                        contentElement.innerHTML = this.formatContent(linksContent)
                        this.scrollToBottom()
                    } else {
                        this.pendingFinalContent = linksContent
                    }
                }
                break

            case 'message_id':
                // Store the persisted message ID so feedback buttons can be added
                messageContainer.dataset.messageId = data
                // If streaming already finished, append feedback buttons now
                if (!messageContainer.classList.contains('streaming')) {
                    messageContainer.appendChild(this.createFeedbackButtons(data))
                    this.scrollToBottom()
                }
                break

            case 'error':
                // Show error message immediately
                this.stopTokenQueue()
                if (contentElement) {
                    contentElement.innerHTML = `<span class="streaming-error">${this.formatContent(data)}</span>`
                }
                messageContainer.classList.remove('streaming')
                messageContainer.classList.add('error-message')
                break

            default:
                console.warn('Unknown SSE event type:', eventType)
        }

        return currentContent
    }

    // Typewriter effect: queue tokens and render character by character
    enqueueToken(token, contentElement) {
        // Remove typing dots on first token
        if (contentElement) {
            const dots = contentElement.querySelector('.typing-dots')
            if (dots) dots.remove()
        }

        // Push individual characters so rendering is smooth
        for (const char of token) {
            this.tokenQueue.push(char)
        }
        this.startTokenQueue(contentElement)
    }

    startTokenQueue(contentElement) {
        if (this.renderInterval) return

        this.renderInterval = setInterval(() => {
            if (this.tokenQueue.length === 0) {
                this.stopTokenQueue()

                // Apply markdown formatting now that all text is rendered
                if (contentElement) {
                    contentElement.innerHTML = this.formatContent(this.renderedContent)
                }

                // If a drain callback is waiting, fire it
                if (this.onQueueDrain) {
                    const cb = this.onQueueDrain
                    this.onQueueDrain = null
                    cb()
                }
                return
            }

            // Gentle adaptive batch: 1-3 chars per tick for a visible typewriter feel
            const batchSize = Math.min(3, Math.max(1, Math.ceil(this.tokenQueue.length / 60)))
            for (let i = 0; i < batchSize; i++) {
                if (this.tokenQueue.length === 0) break
                this.renderedContent += this.tokenQueue.shift()
            }

            if (contentElement) {
                // Use textContent during typewriter to avoid garbled markdown —
                // formatContent is applied once when the queue drains via onQueueDrain
                contentElement.textContent = this.renderedContent
            }
            this.scrollToBottom()
        }, this.TOKEN_DELAY_MS)
    }

    stopTokenQueue() {
        if (this.renderInterval) {
            clearInterval(this.renderInterval)
            this.renderInterval = null
        }
    }

    flushTokenQueue(contentElement, finalContent) {
        this.stopTokenQueue()
        this.tokenQueue = []
        this.renderedContent = finalContent
        this.onQueueDrain = null
        this.pendingFinalContent = null
        if (contentElement) {
            contentElement.innerHTML = this.formatContent(finalContent)
        }
        this.scrollToBottom()
    }

    // Create empty message container for streaming (with typing dots)
    createStreamingMessageContainer() {
        // Reset typewriter state for new message
        this.stopTokenQueue()
        this.tokenQueue = []
        this.renderedContent = ''
        this.pendingFinalContent = null
        this.onQueueDrain = null

        const messageDiv = document.createElement('div')
        messageDiv.className = 'message assistant-message streaming'
        messageDiv.innerHTML = `
            <span class="streaming-content"><span class="typing-dots"><span>.</span><span>.</span><span>.</span></span></span>
            <span class="streaming-cursor"></span>
        `

        this.messagesTarget.appendChild(messageDiv)
        this.scrollToBottom()

        return messageDiv
    }

    // Remove streaming cursor from all messages
    removeStreamingCursor() {
        const cursors = this.messagesTarget.querySelectorAll('.streaming-cursor')
        cursors.forEach(cursor => cursor.remove())

        const streamingMessages = this.messagesTarget.querySelectorAll('.streaming')
        streamingMessages.forEach(msg => msg.classList.remove('streaming'))
    }

    handleSendError(error) {
        console.error('Chat send error:', error)
        if (typeof this.addMessage === 'function') {
            this.addMessage('Sorry, something went wrong sending your message. Please try again.', 'assistant')
        }
    }

    loadMessages(messages) {
        // Clear welcome message
        const welcomeMsg = this.messagesTarget.querySelector('.welcome-message')
        if (welcomeMsg) {
            welcomeMsg.remove()
        }

        // Add messages (with skipRedirect flag to prevent re-redirecting on history load)
        messages.forEach(msg => {
            this.addMessage(msg.content, msg.role, true, msg.id, msg.feedback_rating)
        })
    }

    addMessage(content, role, skipRedirect = false, messageId = null, feedbackRating = null) {
        // Check for redirect instruction (but only if not loading history)
        const redirectMatch = content.match(/\[REDIRECT:([^\]]+)\]/)
        if (redirectMatch && role === 'assistant' && !skipRedirect) {
            const url = redirectMatch[1]
            // Remove the redirect tag but keep all other content including links
            const displayContent = content.replace(/\[REDIRECT:[^\]]+\]/, '')

            const messageDiv = document.createElement('div')
            messageDiv.className = `message ${role}-message`
            messageDiv.innerHTML = this.formatContent(displayContent)

            // Add feedback buttons to assistant messages with an ID
            if (messageId) {
                messageDiv.appendChild(this.createFeedbackButtons(messageId, feedbackRating))
            }

            this.messagesTarget.appendChild(messageDiv)
            this.scrollToBottom()

            // Add a small status message about the redirect
            const redirectMsg = document.createElement('div')
            redirectMsg.style.cssText = 'text-align: center; margin: 10px 0; padding: 8px; background: #f0f9ff; border-radius: 8px; font-size: 13px; color: #0369a1;'
            redirectMsg.innerHTML = 'Opening logbook entry in 1 second... <span style="font-size: 11px;">(or click link above)</span>'
            this.messagesTarget.appendChild(redirectMsg)
            this.scrollToBottom()

            // Redirect after a short delay so user sees the message and can click link if needed
            setTimeout(() => {
                window.location.href = url
            }, 1000)
            return
        }

        // Remove redirect tag from content before displaying (for historical messages)
        const cleanContent = content.replace(/\[REDIRECT:[^\]]+\]/, '')

        const messageDiv = document.createElement('div')
        messageDiv.className = `message ${role}-message`
        messageDiv.innerHTML = this.formatContent(cleanContent)

        // Add feedback buttons to assistant messages with an ID
        if (role === 'assistant' && messageId) {
            messageDiv.appendChild(this.createFeedbackButtons(messageId, feedbackRating))
        }

        this.messagesTarget.appendChild(messageDiv)
        this.scrollToBottom()
    }

    showTypingIndicator() {
        const indicator = document.createElement('div')
        indicator.className = 'typing-indicator'
        indicator.innerHTML = 'AI is typing...'
        indicator.id = 'typing-indicator'

        this.messagesTarget.appendChild(indicator)
        this.scrollToBottom()
    }

    hideTypingIndicator() {
        const indicator = this.messagesTarget.querySelector('#typing-indicator')
        if (indicator) {
            indicator.remove()
        }
    }

    // Create action buttons (copy, thumbs up/down) for an assistant message
    createFeedbackButtons(messageId, existingRating = null) {
        const wrapper = document.createElement('div')
        wrapper.className = 'message-feedback'
        wrapper.dataset.messageId = messageId

        const copyBtn = document.createElement('button')
        copyBtn.className = 'feedback-btn copy-btn'
        copyBtn.innerHTML = '<i class="fa fa-copy"></i>'
        copyBtn.title = 'Copy to clipboard'
        copyBtn.addEventListener('click', () => this.copyMessage(copyBtn))

        const thumbsUp = document.createElement('button')
        thumbsUp.className = 'feedback-btn feedback-up'
        thumbsUp.innerHTML = '<i class="fa fa-thumbs-up"></i>'
        thumbsUp.title = 'Helpful'
        if (existingRating === 1) thumbsUp.classList.add('active')

        const thumbsDown = document.createElement('button')
        thumbsDown.className = 'feedback-btn feedback-down'
        thumbsDown.innerHTML = '<i class="fa fa-thumbs-down"></i>'
        thumbsDown.title = 'Not helpful'
        if (existingRating === -1) thumbsDown.classList.add('active')

        thumbsUp.addEventListener('click', () => this.rateMessage(messageId, 1, wrapper))
        thumbsDown.addEventListener('click', () => this.rateMessage(messageId, -1, wrapper))

        wrapper.appendChild(copyBtn)
        wrapper.appendChild(thumbsUp)
        wrapper.appendChild(thumbsDown)

        return wrapper
    }

    // Copy assistant message text to clipboard, preserving table structure
    copyMessage(button) {
        const messageDiv = button.closest('.message')
        if (!messageDiv) return

        // Get the message content without the feedback buttons
        const clone = messageDiv.cloneNode(true)
        const feedback = clone.querySelector('.message-feedback')
        if (feedback) feedback.remove()

        const text = this.extractCopyText(clone)

        navigator.clipboard.writeText(text).then(() => {
            this.showCopiedFeedback(button)
        }).catch(() => {
            // Fallback for older browsers
            const textarea = document.createElement('textarea')
            textarea.value = text
            textarea.style.position = 'fixed'
            textarea.style.opacity = '0'
            document.body.appendChild(textarea)
            textarea.select()
            document.execCommand('copy')
            document.body.removeChild(textarea)
            this.showCopiedFeedback(button)
        })
    }

    // Extract text from a message element, converting tables to tab-separated format
    extractCopyText(element) {
        const parts = []

        for (const node of element.childNodes) {
            // Handle table wrapper divs
            if (node.nodeType === 1 && node.classList?.contains('chat-table-wrap')) {
                const table = node.querySelector('table')
                if (table) {
                    parts.push(this.tableToTSV(table))
                    continue
                }
            }

            // Handle bare tables (without wrapper)
            if (node.nodeType === 1 && node.tagName === 'TABLE') {
                parts.push(this.tableToTSV(node))
                continue
            }

            // Everything else: use innerText
            const text = node.innerText || node.textContent || ''
            if (text.trim()) parts.push(text.trim())
        }

        return parts.join('\n\n')
    }

    // Convert an HTML table to tab-separated values (pastes correctly into spreadsheets)
    tableToTSV(table) {
        const rows = []
        for (const tr of table.querySelectorAll('tr')) {
            const cells = []
            for (const cell of tr.querySelectorAll('th, td')) {
                cells.push(cell.innerText.trim())
            }
            rows.push(cells.join('\t'))
        }
        return rows.join('\n')
    }

    showCopiedFeedback(button) {
        const icon = button.querySelector('i')
        icon.className = 'fa fa-check'
        button.classList.add('copied')
        setTimeout(() => {
            icon.className = 'fa fa-copy'
            button.classList.remove('copied')
        }, 2000)
    }

    // Send thumbs up/down rating to the server
    async rateMessage(messageId, rating, wrapper) {
        try {
            const response = await fetch(`/messages/${messageId}/rate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': this.getCSRFToken()
                },
                body: JSON.stringify({ rating: rating })
            })

            if (response.ok) {
                const upBtn = wrapper.querySelector('.feedback-up')
                const downBtn = wrapper.querySelector('.feedback-down')
                upBtn.classList.toggle('active', rating === 1)
                downBtn.classList.toggle('active', rating === -1)
            }
        } catch (error) {
            console.error('Failed to rate message:', error)
        }
    }

    formatContent(content) {
        if (!content) return ''

        // Repair malformed markdown tables before parsing.
        // AI models sometimes generate broken separator rows like "|-||||"
        // instead of the correct "|---|---|---|---|". Detect a table header row
        // (line with pipes and text) followed by a broken separator, and fix it.
        const repaired = content.replace(
            /^(\|(?:[^|\n]+\|)+)\s*\n\|[-| ]*\|?\s*$/gm,
            (match, headerRow) => {
                // Count columns from the header row
                const cols = (headerRow.match(/\|/g) || []).length - 1
                if (cols < 1) return match
                const separator = '|' + ' --- |'.repeat(cols)
                return headerRow + '\n' + separator
            }
        )

        // Use marked to render full markdown (tables, lists, code, bold, etc.)
        let html = marked.parse(repaired)

        // Wrap tables in a responsive scroll container
        html = html.replace(/<table>/g, '<div class="chat-table-wrap"><table>')
        html = html.replace(/<\/table>/g, '</table></div>')

        return html
    }

    // Throttled scroll using requestAnimationFrame
    scrollToBottom() {
        if (this._scrollRAF) return
        this._scrollRAF = requestAnimationFrame(() => {
            this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
            this._scrollRAF = null
        })
    }

    setLoading(loading) {
        this.isLoading = loading
        const submitBtn = this.formTarget.querySelector('button[type="submit"]')
        const input = this.inputTarget

        if (submitBtn) {
            submitBtn.disabled = loading
        }
        if (input) {
            input.disabled = loading
        }
    }

    getCSRFToken() {
        const token = document.querySelector('meta[name="csrf-token"]')
        return token ? token.content : ''
    }
}
